import type { Tier, Layer } from '../engine/types';

/**
 * The coverage catalog.
 *
 * Every technique from fingerprinting-techniques.md and
 * novel_bot_detection_techniques.md appears here exactly once, with an honest
 * measurability verdict. This is what the /techniques page renders, and it is
 * what makes the project's claim to "implement all the techniques" auditable
 * rather than a claim.
 *
 * `tier: 'server'` entries are NOT faked as collected signals. They record why
 * the technique is unmeasurable from a static page and what infrastructure
 * would be required.
 */

export interface Technique {
  id: string;
  title: string;
  /** Which of the source docs this came from. */
  doc:
    | 'fingerprinting'
    | 'bot-detection'
    | 'desync-cache'
    | 'security-research'
    | 'creepjs'
    | 'papers'
    | 'baseline'
    | 'dns-cache'
    | 'cert-store';
  /** Section reference in the source doc, e.g. '§6' or '§2A'. */
  section: string;
  tier: Tier;
  layer: Layer;
  /** Implemented = ships as a live probe in this repo. */
  status: 'implemented' | 'partial' | 'not-measurable' | 'planned';
  /** Which probe implements it, when implemented. */
  probe?: string;
  /** One-line description of the signal. */
  summary: string;
  /** For server-tier items: why we cannot measure it, and what would be needed. */
  limitation?: string;
  sources: string[];
}

const FLD: Record<string, string> = {
  gre: 'https://datatracker.ietf.org/doc/html/rfc8701',
  cert: 'https://datatracker.ietf.org/doc/html/rfc8879',
  tls13: 'https://datatracker.ietf.org/doc/html/rfc8446',
  h2: 'https://httpwg.org/specs/rfc9113.html',
  h2prio: 'https://httpwg.org/specs/rfc9218.html',
  hpack: 'https://datatracker.ietf.org/doc/html/rfc7541',
  quic: 'https://datatracker.ietf.org/doc/html/rfc9000',
  quicgrease: 'https://datatracker.ietf.org/doc/html/rfc9287',
  tcprfc: 'https://datatracker.ietf.org/doc/html/rfc9293',
  he: 'https://datatracker.ietf.org/doc/html/rfc8305',
  rfc6724: 'https://datatracker.ietf.org/doc/html/rfc6724',
  rfc4941: 'https://datatracker.ietf.org/doc/html/rfc4941',
  dns64: 'https://datatracker.ietf.org/doc/html/rfc7050',
  webrtc: 'https://www.w3.org/TR/webrtc/#rtcicecandidate-interface',
  fetch: 'https://fetch.spec.whatwg.org/',
  smuggled: 'https://portswigger.net/research/http-parsing-discrepancies',
  caches: 'https://w3c.github.io/ServiceWorker/#cache-interface',
  rfc9111: 'https://www.rfc-editor.org/rfc/rfc9110.html',
  rfc9110: 'https://www.rfc-editor.org/rfc/rfc9110.html',
  hrTime: 'https://www.w3.org/TR/hr-time/',
  hrTimeIsolated: 'https://www.w3.org/TR/2021/NOTE-hr-time-2021-05',
  domDomain: 'https://chromium.googlesource.com/chromium/src/+/refs/heads/main/docs/security/document-domain.md',
  domDomainBlog: 'https://developer.chrome.com/blog/document-domain-setter-deprecation/',
  rfc7323: 'https://datatracker.ietf.org/doc/html/rfc7323',
  kohno: 'https://www.usenix.org/legacy/events/sec05/tech/full_papers/kohno/kohno.pdf',
  p0f: 'https://lcamtuf.coredump.cx/afl/vad02/p0f.pdf',
  rfc6056: 'https://datatracker.ietf.org/doc/html/rfc6056',
  rfc8446psk: 'https://datatracker.ietf.org/doc/html/rfc8446#section-4.2.8',
  kyber: 'https://datatracker.ietf.org/doc/html/draft-ietf-tls-hybrid-design-09',
  ocsp: 'https://datatracker.ietf.org/doc/html/rfc6960',
  aia: 'https://datatracker.ietf.org/doc/html/rfc5280#section-4.2.2.1',
  cors: 'https://fetch.spec.whatwg.org/#http-cors-protocol',
  pnaSource: 'https://developer.chrome.com/blog/private-network-access-preflight/',
  siteIsolation: 'https://developer.chrome.com/blog/site-isolation/',
  spectre: 'https://spectreattack.org/',
  rfc8446: 'https://datatracker.ietf.org/doc/html/rfc8446',
  cssom: 'https://www.w3.org/TR/cssom-1/',
  svg2: 'https://www.w3.org/TR/SVG2/',
  ecma262: 'https://tc39.es/ecma262/',
  moweryJs: 'https://cseweb.ucsd.edu/~kmowery/papers/js-fingerprinting.pdf',
  saitoMath: 'https://meiji.elsevierpure.com/en/publications/on-estimating-platforms-of-web-user-with-javascript-math-object',
  fifieldFonts: 'https://fc15.ifca.ai/preproceedings/paper_83.pdf',
  vadrevuAudio: 'https://arxiv.org/pdf/2107.14201.pdf',
  laperdrixSurvey: 'https://arxiv.org/pdf/1905.01051.pdf',
  shapeShifter: 'https://hal.archives-ouvertes.fr/hal-02612461/document',
  fpStalker: 'https://hal.inria.fr/hal-01652021/document',
  sjoestenWar: 'https://www.usenix.org/system/files/sec22summer_cherubin.pdf',
  soleva: 'https://arxiv.org/pdf/2103.04952.pdf',
  leakingBattery: 'https://eprint.iacr.org/2015/616.pdf',
  gummyBrowsers: 'https://arxiv.org/pdf/2110.10129.pdf',
  drawnApart: 'https://arxiv.org/pdf/2201.09956.pdf',
  niceIcons: 'https://www.cs.uic.edu/~polakis/papers/solomos-ndss21.pdf',
  webglRadar: 'https://w3c.github.io/webgl/extension/WEBGL_debug_renderer_info/',
  unicodeEmoji: 'https://unicode.org/emoji/',
  resourceTiming: 'https://www.w3.org/TR/resource-timing/',
  dnsRebind: 'https://portswigger.net/research/dns-rebinding',
  rfc7873: 'https://datatracker.ietf.org/doc/html/rfc7873',
  rfc7871: 'https://datatracker.ietf.org/doc/html/rfc7871',
  rfc9520: 'https://datatracker.ietf.org/doc/html/rfc9520',
  rfc5280: 'https://datatracker.ietf.org/doc/html/rfc5280',
  rfc6962: 'https://datatracker.ietf.org/doc/html/rfc6962',
  rfc7633: 'https://datatracker.ietf.org/doc/html/rfc7633',
  cabForum: 'https://www.cabforum.org/',
  chromeRootStore: 'https://chromium.googlesource.com/chromium/src/+/main/net/data/ssl/chrome_root_store/root_store.md',
  nssRoots: 'https://security.mozilla.org/certificate-authorities/',
  applePkI: 'https://support.apple.com/en-us/HT210176',
  nikiforakisTampering:
    'https://adtechmadness.wordpress.com/2019/03/23/javascript-tampering-detection-and-stealth/',
  v8site: 'https://v8.dev/',
  intlTimezone: 'https://tc39.es/ecma262/#sec-date-timezone-string',
  ice: 'https://datatracker.ietf.org/doc/html/rfc8445',
  stun: 'https://datatracker.ietf.org/doc/html/rfc8489',
  turn: 'https://datatracker.ietf.org/doc/html/rfc8656',
  dns0x20: 'https://datatracker.ietf.org/doc/html/draft-vixie-dnsext-dns0x20-00',
  dnssec: 'https://datatracker.ietf.org/doc/html/rfc4033',
  dohRfc: 'https://datatracker.ietf.org/doc/html/rfc8484',
  dohJson:
    'https://developers.cloudflare.com/1.1.1.1/encryption/dns-queries/',
  cfRequest: 'https://developers.cloudflare.com/workers/runtime-apis/request/',
  webrtcstats: 'https://www.w3.org/TR/webrtc-stats/',
  webcodecs: 'https://www.w3.org/TR/webcodecs/',
  sab: 'https://webassembly.github.io/threads/core/shared/structs.html',
  coi: 'https://html.spec.whatwg.org/multipage/browsers.html#cross-origin-isolated',
  webgpu: 'https://www.w3.org/TR/webgpu/',
  webaudio: 'https://www.w3.org/TR/webaudio/',
  webidl: 'https://webassembly.github.io/webidl-js-api/',
  ipv6: 'https://datatracker.ietf.org/doc/html/rfc8200',
  chromemdns: 'https://issues.chromium.org/issues/471788748',
  perftiming: 'https://www.w3.org/TR/resource-timing/',
  textmetrics: 'https://www.w3.org/TR/css-fonts-4/#textmetrics',
  webdriver: 'https://w3c.github.io/webdriver/#webdriver-active-flag',
  cdp: 'https://chromedevtools.github.io/devtools-protocol/',
  motion: 'https://www.w3.org/TR/device-motion/',
  htmlperf: 'https://w3c.github.io/hr-time/',
};

export const TECHNIQUES: Technique[] = [
  // ---------------------------------------------------------------- §1 TLS
  {
    id: 'tls.ja4',
    title: 'JA4 / JA3 TLS ClientHello fingerprint',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'edge',
    layer: 'tls',
    status: 'partial',
    probe: 'edge',
    summary:
      'JA3/JA4 hash the ordered cipher + extension lists from the raw ClientHello. JavaScript can never see a ClientHello, so a static page cannot compute these itself.',
    limitation:
      'Free Cloudflare Workers expose only SHA-1 hashes of the cipher and extension lists (tlsClientCiphersSha1 / tlsClientExtensionsSha1), not the ordered lists JA4 requires — so we do a ClientHello signature lookup instead. A true JA4 string is only available under request.cf.botManagement, which is a paid Bot Management add-on. The Worker reads that field opportunistically, so the card upgrades itself to real JA4 if the add-on is ever enabled.',
    sources: ['https://github.com/FoxIO-LLC/ja4', 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'tls.grease-placement',
    title: 'GREASE value placement and rotation',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Chrome/BoringSSL inserts GREASE values in specific ClientHello slots and rotates them. A browser-impersonating library that adds "a" GREASE value in the wrong position, or reuses a static codepoint across sessions, is fingerprintable.',
    limitation:
      'Requires raw ClientHello bytes. A static page behind an edge never sees the handshake.',
    sources: [FLD.gre, 'https://chromestatus.com/'],
  },
  {
    id: 'tls.compress-certificate',
    title: 'Certificate compression (RFC 8879) presence',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Chrome and Firefox advertise compress_certificate; almost no HTTP client library does. A ClientHello claiming to be Chrome without it is an instant tell.',
    limitation: 'Requires raw ClientHello extension list; not exposed by the edge.',
    sources: [FLD.cert],
  },
  {
    id: 'tls.resumption',
    title: 'Extension ordering stability across session resumption',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Real browsers drop or reorder extensions on a resumed handshake (e.g. key_share groups with early PSK); most HTTP libraries send an identical ClientHello regardless of resumption state.',
    limitation: 'Requires observing a resumed handshake server-side, across requests.',
    sources: [FLD.tls13],
  },
  {
    id: 'tls.sig-algo',
    title: 'Signature algorithm list length/ordering',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'The signature_algorithms list differs canonically between Go crypto/tls, BoringSSL, OpenSSL, and rustls. JA3 weights it lightly; JA4 weights it heavily.',
    limitation: 'Requires the raw extension contents, not a hash.',
    sources: [FLD.tls13],
  },
  {
    id: 'tls.0rtt',
    title: '0-RTT eagerness and session-ticket cache size',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Whether a client attempts 0-RTT at all, and how many session tickets it caches per host, is observable via early-data behaviour.',
    limitation: 'Requires tracking handshake state across requests server-side.',
    sources: [FLD.tls13],
  },
  {
    id: 'tls.ec-point-formats',
    title: 'ec_point_formats quirk (OS/distro leak)',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Go omits ec_point_formats; some Python ssl/OpenSSL builds include it depending on distro OpenSSL version, leaking the OS rather than just the language.',
    limitation: 'Requires raw ClientHello extension list.',
    sources: ['https://www.rfc-editor.org/rfc/rfc8422.html'],
  },
  {
    id: 'tls.psk-mode',
    title: 'PSK key exchange mode ordering (psk_dhe_ke vs psk_ke)',
    doc: 'fingerprinting',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary: 'PSK exchange mode ordering differs between BoringSSL, OpenSSL 3.x, and rustls.',
    limitation: 'Requires raw ClientHello bytes.',
    sources: [FLD.tls13],
  },

  // ---------------------------------------------------------- §2 HTTP/1.1
  {
    id: 'http1.header-whitespace',
    title: 'Header value whitespace on semantically identical values',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'edge',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      '"gzip, deflate" vs "gzip,deflate" distinguishes Node http from undici, and Python requests from httpx from aiohttp.',
    limitation:
      'We relay the inbound Accept-Encoding via the edge Worker, reading Cloudflare\'s clientAcceptEncoding (the original client value). Full per-header order is not recoverable because Cloudflare reconstructs the request.',
    sources: [FLD.h2],
  },
  {
    id: 'http1.accept-encoding-zstd',
    title: 'Accept-Encoding br / zstd support and ordering',
    doc: 'fingerprinting',
    section: '§5',
    tier: 'edge',
    layer: 'network',
    status: 'implemented',
    probe: 'edge',
    summary:
      'zstd support is a strong "recent Chrome/curl" signal, since most scripting-language HTTP libraries have not added it. We read the original value from clientAcceptEncoding.',
    limitation: 'Only the Accept-Encoding header is recovered verbatim; other header formatting is normalized by the edge.',
    sources: ['https://www.rfc-editor.org/rfc/rfc8878.html'],
  },
  {
    id: 'http1.content-length',
    title: 'Content-Length vs Transfer-Encoding on zero-length bodies',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'On a bodyless GET, some libraries send Content-Length: 0, some drop it, some omit the header entirely — a library-specific tell.',
    limitation: 'Requires observing a client-constructed request; a page only sees its own request.',
    sources: [FLD.h2],
  },
  {
    id: 'http1.expect-continue',
    title: 'Expect: 100-continue trigger threshold',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'libcurl auto-adds Expect above a body-size threshold; the threshold varies by library/version.',
    limitation: 'Requires observing outbound request bodies from the client.',
    sources: ['https://curl.se/libcurl/c/CURLOPT_EXPECT_100_TIMEOUT_MS.html'],
  },
  {
    id: 'http1.header-injection-order',
    title: 'Automatic header injection order vs user-set headers',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Go net/http special-cases Host/User-Agent/Referer into fixed slots regardless of map order; the per-library slot rules differ enough to serve as a signature.',
    limitation: 'Requires the true inbound header order, which Cloudflare does not preserve.',
    sources: ['https://pkg.go.dev/net/http#Request'],
  },
  {
    id: 'http1.duplicate-header-folding',
    title: 'Duplicate Set-Cookie folding behaviour',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'Whether duplicate headers are concatenated, both kept, or last-wins is highly library-specific.',
    limitation: 'Response-parsing behaviour is not observable from a static page.',
    sources: [FLD.h2],
  },

  // -------------------------------------------------------------- §3 HTTP/2
  {
    id: 'h2.settings-values',
    title: 'SETTINGS frame numeric values (not just presence)',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'INITIAL_WINDOW_SIZE / MAX_CONCURRENT_STREAMS / HEADER_TABLE_SIZE values are library- and version-specific and rarely spoofed even by tools that fix SETTINGS ordering.',
    limitation: 'Requires raw HTTP/2 frame bytes; Cloudflare does not expose them.',
    sources: [FLD.h2],
  },
  {
    id: 'h2.window-update-stream0',
    title: 'Standalone WINDOW_UPDATE on stream 0 after preface',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Chrome sends a stream-0 WINDOW_UPDATE immediately after the connection preface; most bare HTTP/2 libraries do not, or use a different increment.',
    limitation: 'Requires raw HTTP/2 frame capture.',
    sources: [FLD.h2],
  },
  {
    id: 'h2.hpack-indexing',
    title: 'HPACK dynamic table indexing choices',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Browsers reuse the dynamic table aggressively; server-side clients index far less. Visible in raw frame bytes even when decoded headers look identical.',
    limitation: 'Requires raw HPACK block bytes.',
    sources: [FLD.hpack],
  },
  {
    id: 'h2.huffman-threshold',
    title: 'Huffman encoding on values that do not benefit',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'Some encoders always Huffman-encode; others threshold on length, and the threshold differs.',
    limitation: 'Requires raw HPACK block bytes.',
    sources: [FLD.hpack],
  },
  {
    id: 'h2.priority-tree',
    title: 'PRIORITY frame dependency-tree shape (RFC 9218)',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      'Chrome builds a specific placeholder priority tree unrelated to real prioritization; the scheme changed with RFC 9218. A client claiming latest Chrome with the old scheme is a tell.',
    limitation:
      'We do get requestPriority ("weight=…;exclusive=…;group=…") from the edge, which is the RFC 9218 residue, but not the full frame tree.',
    sources: [FLD.h2prio, 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'h2.frame-padding',
    title: 'Padding on DATA/HEADERS frames',
    doc: 'fingerprinting',
    section: '§3',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'Vanishingly rare in non-browser clients; present intermittently in real browsers.',
    limitation: 'Requires raw HTTP/2 frames.',
    sources: [FLD.h2],
  },

  // ------------------------------------------------------- §4 HTTP/3 / QUIC
  {
    id: 'quic.transport-params',
    title: 'QUIC transport parameter ordering and presence',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      'Transport parameter order/values differ meaningfully between quiche, quic-go, msquic, and Chromium.',
    limitation:
      'Cloudflare does not expose transport parameters. We do get httpProtocol === "HTTP/3" and clientQuicRtt when the client connects over QUIC.',
    sources: [FLD.quic, 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'quic.cid-length',
    title: 'Connection ID length choices',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'Chromium tends to 8-byte CIDs; quic-go differs; variable-length CIDs leak the library.',
    limitation: 'Requires raw QUIC Initial packet bytes.',
    sources: [FLD.quic],
  },
  {
    id: 'quic.padding',
    title: 'Initial packet padding to 1200 bytes',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'How padding is applied (trailing PADDING frames vs in-packet) differs, and some stacks under-pad in edge cases — a fingerprintable spec violation.',
    limitation: 'Requires raw QUIC packet capture.',
    sources: [FLD.quic],
  },
  {
    id: 'quic.version-negotiation',
    title: 'QUIC version negotiation list order',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary: 'The order of offered draft/final versions differs by stack.',
    limitation: 'Requires raw QUIC packet capture.',
    sources: [FLD.quic],
  },
  {
    id: 'quic.greasing',
    title: 'QUIC greasing of transport params / frame types (RFC 9287)',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Very few non-browser clients implement greasing. Its absence combined with a Chrome-looking h3 ALPN is a strong non-browser signal.',
    limitation: 'Requires raw QUIC frames.',
    sources: [FLD.quicgrease],
  },

  // ---------------------------------------------------------- §5 cross-layer
  {
    id: 'tcp.ja4t',
    title: 'TCP fingerprint / JA4T (initial window, option order, TTL)',
    doc: 'fingerprinting',
    section: '§5',
    tier: 'edge',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      'A TLS fingerprint claiming a different OS than the TCP stack implies is a powerful composite signal, and most evasion tooling only fixes the TLS/HTTP layers.',
    limitation:
      'Full JA4T needs a TCP listener and is patent-pending under the FoxIO License 1.1, so we do not implement it. We do surface clientTcpRtt and edgeL4.deliveryRate from the edge as partial transport signal.',
    sources: [FLD.tcprfc, 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'dns.happy-eyeballs',
    title: 'DNS resolution order and Happy Eyeballs constant',
    doc: 'fingerprinting',
    section: '§5, §8',
    tier: 'client',
    layer: 'network',
    status: 'planned',
    summary:
      'A-before-AAAA vs racing, and the specific fallback constant, differ by stack (Go defaults to 300 ms). Observable via timing analysis.',
    limitation:
      'A page cannot observe its own resolver race directly; this needs a dual-stack endpoint under our control plus arrival-timing measurement. Deferred to a later phase.',
    sources: [FLD.he],
  },
  {
    id: 'net.dual-stack-rtt',
    title: 'Dual-stack IPv4/IPv6 ASN mismatch and RTT ratio',
    doc: 'bot-detection',
    section: '§1',
    tier: 'edge',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      'A proxying IPv4 path with a direct IPv6 path leaks two ASNs, and RTTv4 ≫ RTTv6 (ratio > 2.5) is a strong proxy signal.',
    limitation:
      'The edge gives us the ASN/geo for the connection that reached it. Comparing a simultaneous v4 and v6 arrival needs two controlled dual-stack hostnames; deferred.',
    sources: [FLD.he, 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'net.redirect-auth',
    title: 'Redirect-following header/Authorization retention',
    doc: 'fingerprinting',
    section: '§5',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Whether cookies/headers/Authorization are stripped across cross-origin redirects is inconsistent even between spec-compliant libraries.',
    limitation: 'Requires observing a client\'s redirect chain server-side.',
    sources: ['https://developer.mozilla.org/en-US/docs/Web/HTTP/Redirection'],
  },

  // ---------------------------------------------------------- §6 STUN/WebRTC
  {
    id: 'ice.stun-attribute-order',
    title: 'STUN Binding Request attribute ordering/presence',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'libwebrtc always includes PRIORITY, ICE-CONTROLLING/CONTROLLED, USERNAME, MESSAGE-INTEGRITY, FINGERPRINT in a consistent order; minimal STUN clients omit ICE-CONTROLLING or reorder USERNAME. A passive STUN listener can bucket clients by this alone.',
    limitation:
      'Requires running a STUN server (e.g. coturn) and observing the Binding Request. This is the single highest-value technique in the source doc and it needs server infrastructure a static page cannot provide.',
    sources: [FLD.stun, FLD.ice],
  },
  {
    id: 'ice.txn-id-entropy',
    title: 'STUN transaction ID entropy source',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'libwebrtc uses a CSPRNG per PeerConnection; minimal STUN clients sometimes use a counter or low-entropy PRNG, detectable over multiple requests.',
    limitation: 'Requires observing multiple STUN requests from the same source.',
    sources: [FLD.stun],
  },
  {
    id: 'ice.software-attribute',
    title: 'STUN SOFTWARE attribute string',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'Many libraries populate SOFTWARE with their name/version even when impersonating a browser; browsers generally omit it.',
    limitation: 'Requires a STUN listener to see the attribute.',
    sources: [FLD.stun],
  },
  {
    id: 'ice.candidate-gathering',
    title: 'ICE candidate gathering order and mDNS obfuscation',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'client',
    layer: 'webrtc',
    status: 'implemented',
    probe: 'webrtc',
    summary:
      'libwebrtc gathers host, then srflx, then relay with specific priority tie-breaking, and Chrome adds mDNS-obfuscated (*.local) host candidates by default. The absence of mDNS candidates from something claiming to be Chrome is a strong signal.',
    sources: [FLD.webrtc, FLD.ice, FLD.chromemdns],
  },
  {
    id: 'ice.dtls-srtp',
    title: 'DTLS-SRTP handshake fingerprint',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'A patched headless Chrome may fix the HTTPS JA3 but leave the WebRTC DTLS ClientHello with a different, unmasked fingerprint.',
    limitation: 'Requires terminating SRTP / observing the DTLS handshake.',
    sources: ['https://datatracker.ietf.org/doc/html/rfc5764'],
  },
  {
    id: 'ice.turn-lifetime',
    title: 'TURN allocation lifetime and refresh timing',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'Chrome requests a specific default lifetime and refreshes at a jittered fraction before expiry; custom clients often use the RFC default or a hardcoded 600 s refreshed at exactly 50%.',
    limitation:
      'Not measurable, and the tier was previously wrong. The source technique fingerprints a client’s TURN REFRESH CADENCE (Chrome refreshes at a jittered fraction before expiry; libraries often use a hardcoded round number). Measuring our OWN refresh cadence would tell us nothing about how other clients behave, and fingerprinting them requires TURN server-side allocation logs — i.e. a TURN server you operate. Corrected from client/partial, which overstated this.',
    sources: [FLD.turn],
  },
  {
    id: 'ice.generation-attr',
    title: 'ICE generation / network-cost attributes',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'server',
    layer: 'webrtc',
    status: 'not-measurable',
    summary:
      'Chrome-specific ICE attributes whose presence/values are libwebrtc-version-specific and never replicated by non-Chromium stacks — a high-confidence "is this Chromium" check independent of User-Agent.',
    limitation:
      'Not measurable, and the tier was previously wrong. These ATTRIBUTES appear only in the STUN Binding Request itself, so reading their values requires a STUN listener — server-tier infrastructure, not something a page can observe about its own connection. The webrtc probe can only confirm the client is libwebrtc-based at all (via candidate gathering shape and mDNS obfuscation), which is a much weaker and different claim. Corrected from client/partial, which overstated this.',
    sources: [FLD.ice, 'https://chromestatus.com/'],
  },

  // ------------------------------------------------------ §7 UDP vs TCP
  {
    id: 'transport.quic-fallback',
    title: 'QUIC-vs-TCP fallback timing',
    doc: 'fingerprinting',
    section: '§7',
    tier: 'edge',
    layer: 'network',
    status: 'partial',
    probe: 'edge',
    summary:
      'Browsers race QUIC against TCP/TLS with a browser-specific fallback timeout; a server that black-holes UDP can time when the TCP SYN arrives.',
    limitation:
      'We do get clientQuicRtt vs clientTcpRtt from the edge, which is the observable residue of this, but not the race constant itself.',
    sources: [FLD.quic, 'https://developers.cloudflare.com/workers/runtime-apis/request/'],
  },
  {
    id: 'transport.tcp-nodelay',
    title: 'TCP_NODELAY / small-packet coalescing',
    doc: 'fingerprinting',
    section: '§7',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Go net/http sets TCP_NODELAY by default; some Python socket tools do not, producing measurably different small-packet coalescing in split bodies.',
    limitation: 'Requires kernel-level packet capture on a TCP listener.',
    sources: [FLD.tcprfc],
  },

  // -------------------------------------------------- §8 IPv4 vs IPv6 detail
  {
    id: 'ipv6.flow-label',
    title: 'IPv6 flow label usage',
    doc: 'fingerprinting',
    section: '§8',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Few user-space libraries set a non-zero IPv6 flow label; a consistently computed one hints at OS/kernel version and can contradict a spoofed UA.',
    limitation: 'Requires packet-level IPv6 header inspection.',
    sources: [FLD.ipv6],
  },
  {
    id: 'ipv6.extension-headers',
    title: 'IPv6 extension header presence/order',
    doc: 'fingerprinting',
    section: '§8',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Almost never sent by application libraries; unexpected Hop-by-Hop/Destination-Options headers on "vanilla browser" traffic is a proxy signal.',
    limitation: 'Requires packet-level IPv6 inspection.',
    sources: [FLD.ipv6],
  },
  {
    id: 'dns.dnssec-validation-differential',
    title: 'DNSSEC validation behaviour of a resolver (AD flag + RRSIG presence)',
    doc: 'dns-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'doh',
    summary:
      'The AD (Authenticated Data) bit in a DNS response is only ever set when the resolver actually validated the DNSSEC chain; a non-validating resolver must leave it clear. Querying a signed domain with DO=1 through a DNS-over-HTTPS JSON API therefore distinguishes validating from non-validating resolvers without any packet capture, and the presence of RRSIG (type 46) records in the answer is direct corroborating evidence. This is the client-visible half of dns.edns0-fingerprint’s DO-bit clause: the DO bit itself is query-side and invisible, but the AD flag is the response-side consequence of the same configuration.',
    limitation:
      'This fingerprints the PUBLIC resolver we queried, not the visitor’s own resolver — a DoH endpoint is a replacement resolver, so it tells us nothing about what the visitor’s ISP or OS resolver does. It contacts two free third-party resolvers, which see the visitor’s IP as they would for any client. EDNS0 buffer size and option order remain server-tier: those are query-side and no JSON API exposes them.',
    sources: [FLD.dnssec, FLD.dohRfc, FLD.dohJson],
  },
  {
    id: 'dns.doh-name-echo',
    title: 'DoH endpoint query-name echo behaviour',
    doc: 'dns-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'doh',
    summary:
      'A deliberately mixed-case query name reveals how a DoH endpoint serialises the question: exact echo, lower-casing, and trailing-dot handling are three distinct behaviours. IMPORTANT: this is a property of the ENDPOINT, not a test of 0x20 query-name randomisation (draft-vixie-dnsext-dns0x20-00). 0x20 is applied by a resolver on its queries toward authoritative servers and is invisible through a JSON API. An earlier revision called this a "0x20 fingerprint" and cited RFC 8482; both were wrong.',
    limitation:
      'This describes the PUBLIC DoH endpoint queried, not the visitor’s own resolver: a DoH endpoint replaces their resolver rather than observing it, and the endpoint’s upstream 0x20 behaviour is not observable either. dns.0x20-encoding remains server-tier for the same reason. It contacts two free third-party resolvers, disclosed on the Dashboard.',
    sources: [FLD.dns0x20, FLD.dohRfc],
  },
  {
    id: 'net.egress-ip-split',
    title: 'STUN reflexive address vs edge-observed client address (split-tunnel detection)',
    doc: 'fingerprinting',
    section: '§6',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'doh',
    summary:
      'A public STUN server reports the source address it saw, so a page learns its own public IP over UDP/ICE with no server it operates. The edge separately records the address its HTTPS request arrived from. A single network path yields one public IP for both; a mismatch means UDP and TCP egress via different routes, which is the signature of a split-tunnel VPN, a multi-homed proxy, or an operator routing WebRTC separately. Because it is a contradiction between two independent observers rather than a value, it is a stronger signal than either address alone.',
    limitation:
      'Requires the edge Worker to be deployed (for the connecting IP) and the STUN path to be unobstructed; both default to on, so it works on a pure static host. IPs are never hashed into the fingerprint — they change whenever the visitor’s network does — so only the agreement verdict feeds the score. The literal addresses are exposed in the raw signal list for the reader.',
    sources: [FLD.stun, FLD.ice, FLD.cfRequest],
  },
  {
    id: 'ipv6.dns64',
    title: 'DNS64/NAT64 synthesis and ipv4only.arpa discovery',
    doc: 'fingerprinting',
    section: '§8',
    tier: 'client',
    layer: 'network',
    status: 'planned',
    summary:
      'NAT64 clients get synthesized AAAA records in 64:ff9b::/96; browsers commonly perform the RFC 7050 ipv4only.arpa discovery, most HTTP libraries never do.',
    limitation:
      'Previously deferred with the reasoning that "a DoH JSON API" could unblock it. That was wrong, and testing it is why this entry is still `planned` rather than `implemented`. DNS64 synthesis happens at the RESOLVER, not at the client: querying ipv4only.arpa through a public DoH endpoint returns the RFC 7050 A records (192.0.0.170/171) and an empty AAAA, because those resolvers run native dual-stack. That answers a question about the resolver, not about the visitor’s NAT64 network. Detecting DNS64 on the visitor still requires seeing their own resolver synthesize a 64:ff9b::/96 AAAA, which a page cannot do. The DoH probe does report the ipv4only.arpa answer shape as a resolver-capability fingerprint, which is a different and much weaker claim.',
    sources: [FLD.dns64],
  },
  {
    id: 'ipv6.source-selection',
    title: 'RFC 6724 source address selection deviations',
    doc: 'fingerprinting',
    section: '§8',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Browsers prefer temporary/privacy IPv6 addresses (RFC 4941); containerized farms often only have stable addresses. A non-rotating source across many "different users" is a correlation signal.',
    limitation: 'The observable is the client’s IPv6 SOURCE ADDRESS across sessions: a real consumer device rotates temporary/privacy addresses (RFC 4941), while a container or farm tends to reuse one stable address. Distinguishing them requires logging the source v6 address of many requests from purported-distinct visitors over time and correlating — i.e. cross-session history the page itself does not have and this project does not keep.',
    sources: [FLD.rfc6724, FLD.rfc4941],
  },

  // ------------------------------------------- novel_bot_detection §2 low-level
  {
    id: 'engine.webcodecs',
    title: 'WebCodecs hardware vs software video decode',
    doc: 'bot-detection',
    section: '§2A',
    tier: 'client',
    layer: 'media',
    status: 'implemented',
    probe: 'codecs',
    summary:
      'The primary signal is an explicit query — VideoDecoder.isConfigSupported with hardwareAcceleration:"prefer-hardware" for H.264 baseline, VP9 and AV1. The secondary signal is the timed decode of one synthetic 1280x720 frame (not 4K) and its output pixel format. No hardware path for any of the three, plus software-like timing and an I420 output, is the container/headless signature; timing alone is not evidence and the finding is unscored until validated on real GPU hardware.',
    sources: [FLD.webcodecs],
  },
  {
    id: 'engine.microtask-order',
    title: 'Micro-task execution order under main-thread pressure',
    doc: 'bot-detection',
    section: '§2B',
    tier: 'client',
    layer: 'engine',
    status: 'implemented',
    probe: 'engineTiming',
    summary:
      'Stealth browsers proxy native methods before page load. Even when the proxy returns a spoofed value, the extra JS frames shift micro-task tick counts and ordering relative to native C++ bindings. We interleave Promise.resolve / queueMicrotask / MutationObserver / MessageChannel work, record whether the task order is stable across two identical passes, and check the micro-task portion against the spec’s enqueue order (queueMicrotask → promise → MutationObserver; verified in Chromium, Firefox and WebKit). Macro-task order is deliberately not asserted. Reported, never scored.',
    sources: ['https://html.spec.whatwg.org/multipage/webappapis.html#event-loops', FLD.cdp],
  },
  {
    id: 'engine.font-shaper',
    title: 'OS font engine sub-pixel shaping metrics',
    doc: 'bot-detection',
    section: '§2C',
    tier: 'client',
    layer: 'engine',
    status: 'implemented',
    probe: 'fonts',
    summary:
      'Canvas noise defeats image hashing, but TextMetrics still leaks something coarser: the width-per-em ratios of a few scripts at fractional sizes. A minimal font set — common in containers — falls back to default widths that differ from a real desktop, and a profile mismatch against the claimed OS is a supporting signal. The buckets are coarse, bounding-box values are recorded but not used for the verdict, and the finding is a small-scored (10) heuristic rather than an engine identification.',
    sources: [FLD.textmetrics, 'https://harfbuzz.github.io/'],
  },
  {
    id: 'engine.sab-isolation',
    title: 'Process isolation and SharedArrayBuffer limits',
    doc: 'bot-detection',
    section: '§2D',
    tier: 'client',
    layer: 'sab',
    status: 'implemented',
    probe: 'sab',
    summary:
      'Headless runners and cloud sandboxes often disable process isolation or cap SAB/Wasm memory. We require cross-origin isolation, then probe SharedArrayBuffer allocation, Atomics.waitAsync latency, and wasm memory growth.',
    sources: [FLD.sab, FLD.coi, FLD.webidl],
  },
  {
    id: 'gpu.webgpu-limits',
    title: 'WebGPU adapter capability matrix',
    doc: 'bot-detection',
    section: '§2E',
    tier: 'client',
    layer: 'gpu',
    status: 'implemented',
    probe: 'webgpu',
    summary:
      'WebGPU maps to Vulkan/Metal/DirectX 12 and gives a second view of the GPU/OS layer that WebGL shims often do not touch. adapter.limits and adapter.info catch some UA spoofing, but browsers bucket several limits, so an identical set identifies a device class rather than exact hardware. This project makes no claim that anti-detect browsers ignore WebGPU.',
    sources: [FLD.webgpu],
  },

  // ------------------------------------- novel_bot_detection §3 micro-behaviour
  {
    id: 'behaviour.audio-jitter',
    title: 'Multi-threaded AudioWorklet jitter',
    doc: 'bot-detection',
    section: '§3A',
    tier: 'client',
    layer: 'hardware',
    status: 'implemented',
    probe: 'audio',
    summary:
      'Bare-metal desktops keep the real-time audio thread tightly bounded; VMs, headless instances, and containers show higher jitter variance and hypervisor-style micro-bursts. We timestamp the ARRIVAL of AudioWorklet messages on the main thread and report mean, sigma, p99 and burst count from those wall-clock gaps — the worklet’s own currentTime advances by a fixed 128-frame quantum and is not used. A suspended context is reported as suspended, not as null statistics, and the finding is unscored until the threshold is validated on real hardware.',
    sources: [FLD.webaudio],
  },
  {
    id: 'behaviour.motion-noise',
    title: 'Device motion sensor noise patterns',
    doc: 'bot-detection',
    section: '§3B',
    tier: 'client',
    layer: 'hardware',
    status: 'implemented',
    probe: 'motion',
    summary:
      'Physical MEMS sensors show continuous stochastic noise, zero-g offset drift, and thermal variance. Emulated environments in DevTools or cloud farms often return static vectors like 0,0,9.81 or a perfectly smooth synthetic sine. We classify on the correlation of FIRST DIFFERENCES (detrended), which no longer flags slow smooth real movement, plus variance and vector uniqueness.',
    sources: [FLD.motion],
  },
  {
    id: 'behaviour.dom-latency',
    title: 'DOM interaction latency vs event-loop backpressure',
    doc: 'bot-detection',
    section: '§3C',
    tier: 'client',
    layer: 'behaviour',
    status: 'implemented',
    probe: 'behaviour',
    summary:
      'CDP/Selenium-synthesized interactions bypass the OS input queue and compositor synchronization, arriving with artificial sub-millisecond timestamps. We passively compare event.timeStamp against handler execution time. Opt-in only, and never used for the default score.',
    sources: [FLD.cdp, FLD.htmlperf],
  },

  // -------------------------------- parity baseline (NOT from a source doc)
  // These four are the reference-site parity set. They are standard,
  // well-published detection techniques, but none of them originates in the
  // four research documents, so they are labelled 'baseline' rather than being
  // mis-attributed to a doc section that does not mention them.
  {
    id: 'parity.webdriver',
    title: 'navigator.webdriver and automation globals',
    doc: 'baseline',
    section: 'parity',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'automation',
    summary:
      'The headline automation indicator, alongside CDP stack artifacts and leaked automation globals (playwright, puppeteer, selenium, webdriver, callPhantom, __driver_evaluate).',
    sources: [FLD.webdriver, FLD.cdp],
  },
  {
    id: 'parity.gl-renderer',
    title: 'WebGL unmasked renderer (software rasterizer tell)',
    doc: 'baseline',
    section: 'parity',
    tier: 'client',
    layer: 'gpu',
    status: 'implemented',
    probe: 'webgl',
    summary:
      'HEADLESS_CHROME, SwiftShader, and llvmpipe renderer strings are the single most common automation tell, and are rarely patched even by anti-detect browsers.',
    sources: ['https://developer.chrome.org/blog/headless/', 'https://docs.mesa3d.org/drivers/llvmpipe.html'],
  },
  {
    id: 'parity.coherence',
    title: 'Claimed identity vs observed properties',
    doc: 'baseline',
    section: 'parity',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'coherence',
    summary:
      'A UA claiming Chrome on Windows alongside an Apple Metal WebGL renderer and MacIntel platform is not suspicious on its own — the disagreement is the signal. We check UA vs platform, UA vs GL renderer, UA vs client hints, and DPR vs screen size.',
    sources: ['https://w3c.github.io/webappsec-privacy-considerations/'],
  },
  {
    id: 'http1.line-ending-tolerance',
    title: 'Line-ending tolerance in the response parser (\\r vs \\r\\n)',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'lineEndings',
    summary:
      'A tolerant response parser accepts a bare-CR-terminated header block where a strict one rejects it. That tolerance is engine-specific and a malformed server can distinguish tolerant from strict clients. A page can half-observe this: the browser’s own parser is what must accept (or reject) the response it is served, but the origin must be able to emit the malformed bytes on the wire.',
    limitation:
      'Needs a cooperating raw HTTP/1.1 origin; off by default. A Cloudflare Worker cannot emit a bare-CR header block (the platform normalises response headers), and over HTTP/2 or HTTP/3 line endings are not part of the wire format at all. The probe only exercises anything when VITE_CR_PROBE_ENDPOINT points at a raw HTTP/1.1 server — scripts/cr-probe-server.mjs is a reference implementation. Without it the probe stays silent and measures nothing.',
    sources: [FLD.rfc9110, FLD.fetch],
  },
  {
    id: 'http1.connection-token-case',
    title: 'Connection token case sensitivity on outbound requests',
    doc: 'fingerprinting',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Whether a client emits Connection: keep-alive or Connection: Keep-Alive differs by library and is a cheap proxy-fingerprint. It is the casing sibling of the header-whitespace signal.',
    limitation:
      'Connection is a forbidden request header, so the browser manages it entirely and script cannot set or observe the token casing it emits. This requires raw socket control (curl/Python) against a server you control.',
    sources: [FLD.rfc9110, FLD.fetch],
  },
  {
    id: 'quic.retry-token-resend',
    title: '0-RTT QUIC retry-token handling and transport-param resend',
    doc: 'fingerprinting',
    section: '§4',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Whether a client resends transport parameters identically on a retried Initial packet, and how it handles 0-RTT retry tokens, is a QUIC-stack implementation detail. The QUIC-side counterpart to the TLS-side tls.0rtt signal.',
    limitation:
      'Requires packet-level observation of QUIC Initial retries on a connection you terminate. No browser API exposes retry-token or transport-parameter resend behaviour.',
    sources: [FLD.quic, FLD.tls13],
  },
  {
    id: 'net.backoff-jitter',
    title: 'Retry/backoff jitter shape (or its absence)',
    doc: 'fingerprinting',
    section: '§5',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'A perfectly regular exponential backoff with zero jitter is a behavioural fingerprint detectable by timing alone, with no payload inspection needed. Most HTTP client libraries ship a specific default backoff and jitter algorithm (Go’s, urllib3’s, axios’s), so the inter-retry timing identifies the stack. The jitter-shape sibling of sec.retry-after.',
    limitation:
      'Requires observing a client across MANY retries of a failing request, server-side, over time. A single page load never generates a backoff curve, and a browser does not auto-retry fetch at all — so this is a library-behaviour fingerprint, not a page-observable one.',
    sources: [FLD.rfc9110, FLD.fetch],
  },
  {
    id: 'transport.udp-padding-checksum',
    title: 'UDP checksum and small-packet padding habits (QUIC/STUN)',
    doc: 'fingerprinting',
    section: '§7',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Some custom QUIC/STUN stacks do not pad small packets the way spec-compliant or browser stacks do, making minimum packet size a wire-level differentiator independent of application content; UDP checksum behaviour varies likewise. The general-packet counterpart to quic.padding, which covers only the QUIC Initial 1200-byte rule.',
    limitation:
      'Requires packet capture of UDP datagrams to or from the client. No browser API exposes outbound packet size or checksum behaviour.',
    sources: [FLD.quic, FLD.stun],
  },
  {
    id: 'transport.multi-probe-order',
    title: 'Simultaneous multi-transport (TCP vs QUIC) probing order',
    doc: 'fingerprinting',
    section: '§7',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'network',
    summary:
      'Tools that try both raw TCP and QUIC to the same host sometimes fire them in immediate parallel, while many scripting-language HTTP/3 wrappers only attempt QUIC after full TCP failure. That ordering — parallel vs serial — is observable via connection-arrival timing on both ports. The ordering sibling of transport.quic-fallback, which covers only the fallback timeout constant.',
    limitation:
      'A page can observe the ALPN/protocol the browser chose (httpProtocol / nextHopProtocol), which is the coarse residue of this ordering, but it cannot observe the parallel-vs-serial arrival ordering of its own TCP and QUIC attempts. That needs arrival timing measured at both ports, server-side.',
    sources: [FLD.quic, FLD.rfc9110],
  },
  {
    id: 'ipv6.preference-persistence',
    title: 'Dual-stack connection-preference persistence',
    doc: 'fingerprinting',
    section: '§8',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'dualStack',
    summary:
      'The technique needs the ADDRESS FAMILY of each connection, and page JavaScript cannot observe it: fetch exposes no remote address, and nextHopProtocol reports only the HTTP version (h3 does not imply IPv6). The probe therefore reports only the negotiated application protocol (h3 vs h2/h1) and draws no v6/v4 verdict.',
    limitation:
      'Address family is not exposed to page JavaScript, so preference persistence cannot be measured client-side. It would need a server observing the connecting address family of repeated requests to a dual-stack host; even there, a happy-eyeballs host with a working v6 path has no failure to remember.',
    sources: [FLD.he, FLD.rfc6724],
  },
  {
    id: 'parity.cross-context',
    title: 'Cross-context (main / iframe / worker) divergence',
    doc: 'baseline',
    section: 'parity',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'coherence',
    summary:
      'Automation frameworks and anti-detect browsers often patch only the main context and forget iframes or workers. A genuine browser reports identical values in all three.',
    sources: [FLD.cdp],
  },

  // ------------------------------------ desync-cache-fingerprinting.md §1
  {
    id: 'desync.cl-te-precedence',
    title: 'CL / TE precedence and loser-header disposition',
    doc: 'desync-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'When both Content-Length and Transfer-Encoding are present, which one an intermediary treats as authoritative — and whether the loser is stripped, renamed, passed through, or rejected — is implementation-specific. HAProxy, nginx, Envoy, Apache and the major CDN edges have each made different choices, so the behaviour is a vendor/version fingerprint of the proxy.',
    limitation:
      'The Fetch standard lists Content-Length, TE, Trailer and Transfer-Encoding as forbidden request headers: a browser silently drops them before the request leaves. JavaScript therefore CANNOT send an ambiguous-framing request at all — not a CORS limitation, a user-agent one. This requires raw socket control (curl/Python), so it belongs in an authorized server-side tester, never a client page.',
    sources: [FLD.fetch, FLD.smuggled, FLD.rfc9110],
  },
  {
    id: 'desync.0cl-te0',
    title: '0.CL / TE.0 variant resolution',
    doc: 'desync-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Content-Length: 0 alongside a chunked body (or the reverse) resolves differently per parser: one silently drops the body, one 400s, one desyncs. Which behaviour you get is fingerprint data independent of whether you exploit it.',
    limitation:
      'Same hard boundary as CL/TE — Content-Length and Transfer-Encoding are both forbidden request headers, so a browser will not transmit this ambiguity.',
    sources: [FLD.fetch, FLD.smuggled],
  },
  {
    id: 'desync.malformed-te',
    title: 'Malformed Transfer-Encoding values + unicode-homoglyph header names',
    doc: 'desync-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'How an intermediary reacts to "chunked, chunked", "xchunked", tab/space-obfuscated Transfer-Encoding, or unicode-homoglyph header names (accept / reject / silently normalize) segments proxy populations more precisely than header-order fingerprints, because these are edge cases patched over time — so the fingerprint doubles as a rough patch-level or proxy-age signal.',
    limitation:
      'This source bullet actually contains TWO techniques with DIFFERENT blockers, and conflating them hides a real opportunity. (a) Transfer-Encoding VALUES (chunked, chunked / xchunked / obfuscated) are unreachable because Transfer-Encoding is a forbidden request header that the browser strips at dispatch, regardless of value. (b) unicode-homoglyph HEADER NAMES are NOT covered by that rule — the forbidden-header filter blocks an explicit name list plus the literal "Sec-"/"Proxy-" prefixes, so a homoglyph name (e.g. a Cyrillic "а" substituted into an ASCII header name) is not on the list and can in principle be set and dispatched. We have NOT implemented (b); recording it explicitly so the gap is visible rather than silently swallowed by (a)’s easy justification. Implementing it needs a cooperating origin that reflects back the header names it actually received.',
    sources: [FLD.fetch, FLD.smuggled, FLD.rfc9110],
  },
  {
    id: 'desync.header-blocklist',
    title: 'User-agent forbidden-header enforcement (integrity signal)',
    doc: 'desync-cache',
    section: '§1',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'framing',
    summary:
      'The flip side of the CL/TE boundary: a correct browser silently drops every forbidden request header (Content-Length, Transfer-Encoding, Host, Cookie, Referer, TE, Connection, Proxy-*, Sec-*). A stripped/hooked fetch that lets one through is a tampering signal, and a browser that drops MORE than the spec requires (or is missing Headers enforcement entirely) is a custom or proxied stack.',
    limitation:
      'We can only verify the block happened, not what an intermediary would have done with it. The probe deliberately attempts the injection and reports what survived — it never actually transmits an ambiguous request.',
    sources: [FLD.fetch],
  },
  {
    id: 'desync.header-normalization',
    title: 'Header name/value normalization by the user agent',
    doc: 'desync-cache',
    section: '§1',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'framing',
    summary:
      'Before any request leaves, the user agent lowercases header names, strips leading/trailing HTTP whitespace, and rejects CR/LF/NUL in values. Observing that normalization — and which values survive it — fingerprints the UA stack itself, and any fetch-level hook that bypasses it is visible as a divergence from spec behaviour.',
    limitation:
      'Client-side only. The real target of this research (a forward proxy or MITM appliance re-framing traffic) sits outside the page, so this is the browser-side half of the signal.',
    sources: [FLD.fetch],
  },
  {
    id: 'desync.rejection-timing',
    title: 'Rejection timing side channel on ambiguous requests',
    doc: 'desync-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Even when a WAF correctly rejects an ambiguous request, HOW it rejects leaks implementation detail: some buffer the full declared Content-Length before inspecting the body, others reject on seeing conflicting headers, producing measurably different response latency.',
    limitation:
      'Requires sending the ambiguous request, which a browser will not do. The timing channel is only meaningful against a server you are authorized to test.',
    sources: [FLD.fetch, FLD.smuggled],
  },
  {
    id: 'desync.response-smuggling',
    title: 'Response-side framing ambiguity (what a client proxy re-frames)',
    doc: 'desync-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'The more directly applicable half for client fingerprinting: the server crafts ambiguous framing in the RESPONSE and observes how a client-side forward proxy or transparent MITM appliance re-frames what it ultimately hands to the real client. This identifies what sits between you and the eventual human or bot.',
    limitation:
      'Requires the page to receive a crafted response AND a client-side proxy to re-frame it. The first part is implementable on a server you control; the second is not observable from inside the page. The source doc is explicit that this is "Browser-Powered Desync Attack" territory — authorized testing only, never a public scanner.',
    sources: [FLD.smuggled, FLD.fetch],
  },

  // ------------------------------------ desync-cache-fingerprinting.md §2
  {
    id: 'cache.unkeyed-headers',
    title: 'Unkeyed request headers in the CDN cache key',
    doc: 'desync-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Which headers a given CDN/reverse proxy excludes from the cache key by default is vendor- and config-specific (Cloudflare, Fastly, Akamai, Varnish, nginx proxy_cache all differ), and it is observable from the client against a server you control: a header the cache ignores produces a cache HIT whose body is stale w.r.t. that header.',
    limitation:
      'This is a server-side property of a cache in front of the CLIENT (or of an origin you operate). The browser Cache API is not a CDN cache and keys entries by URL only, ignoring request headers entirely, so nothing page-side can observe a CDN cache key. Needs a purpose-built origin plus hit/miss telemetry.',
    sources: [FLD.caches, FLD.rfc9111],
  },
  {
    id: 'cache.url-normalization',
    title: 'Cache-key URL normalization (case, trailing slash, query order, %2e%2e)',
    doc: 'desync-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'cache',
    summary:
      'Path case sensitivity, trailing-slash handling, query-parameter order sensitivity, and how %2e%2e / double-encoding is normalized before hashing into a cache key all differ across Varnish, Fastly VCL defaults and CloudFront, and are observable there via cache HIT vs MISS.',
    limitation:
      'The shipped measurement is the browser-side half only: the Cache API matching rule is SPEC-DEFINED (a lookup matches the URL string), so conformant browsers are expected to return identical results and this is a conformance check rather than a fingerprint. CDN cache-key normalization is server-side and needs hit/miss telemetry against an origin you control.',
    sources: [FLD.caches, FLD.rfc9111],
  },
  {
    id: 'cache.vary-handling',
    title: 'Vary handling and response collapsing',
    doc: 'desync-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'How a cache handles Vary: Accept-Encoding vs Vary: * vs no Vary — and whether it correctly fragments cached responses per the varied header, or incorrectly collapses them — is both a known poisoning bug class and a clean product/version fingerprint. A browser that does NOT vary the HTTP cache on Accept-Encoding is a detectable, patchable tell.',
    limitation:
      'Accept-Encoding is a FORBIDDEN request header (Fetch spec), so page script cannot vary it — the browser always sends its own set, and the old browser-side "wide vs narrow" check therefore measured nothing (verified in production: both requests returned identical encodings). Observing a cache or CDN product’s Vary behaviour needs a purpose-built origin plus hit/miss telemetry, not a page.',
    sources: [FLD.caches, FLD.rfc9111],
  },
  {
    id: 'cache.heuristic-freshness',
    title: 'Heuristic freshness TTL (RFC 9111)',
    doc: 'desync-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'cache',
    summary:
      'With no explicit Cache-Control/Expires, caches apply heuristic freshness as a vendor-tunable fraction (~10%) of the Last-Modified age. Measuring how long an undirectived object stays fresh fingerprints the caching product/configuration.',
    limitation:
      'Our own _headers sets no caching directives, so we can measure the browser-side heuristic for our own assets. Discriminating a CDN product needs a response with Last-Modified and no explicit freshness, which a static host does not produce.',
    sources: [FLD.rfc9111, FLD.caches],
  },
  {
    id: 'cache.sybil-farm-correlation',
    title: 'Cache-timing as a passive farm / Sybil detector',
    doc: 'desync-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'If many "distinct clients" (different upstream fingerprints) fetch the same resource with cache-hit timing characteristic of ONE shared cache, that correlation indicates a bot farm funnelled through a single caching egress layer — a strong Sybil signal that needs no TLS or HTTP-framing fingerprint at all.',
    limitation:
      'This is fundamentally a cross-visitor, server-side correlation: it requires the site to observe many sessions over time and compare their cache-hit timing. A single client page can only observe its own timing, so this belongs in a server-side analytics pipeline, not a fingerprint collector.',
    sources: [FLD.rfc9111, FLD.caches],
  },

  // ------------------------- security-research-fingerprinting.md §1 side channels
  {
    id: 'sec.timer-resolution',
    title: 'performance.now() clamping granularity as a positive fingerprint',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'engine',
    status: 'implemented',
    probe: 'timerResolution',
    summary:
      'Since Chrome 91, performance.now() is clamped to 100 µs in a non-cross-origin-isolated page and relaxed to 5 µs when isolated; Firefox clamps coarser still and privacy.resistFingerprinting pushes it to 1 ms. The specific granularity is a clean engine + security-configuration signature — it tells you both which engine and whether COOP/COEP enforcement is actually taking effect.',
    limitation:
      'Measures the main-context timer only. Per-origin-isolation and per-frame differences are not observable from the top document, and privacy browsers deliberately poison this value, so it is reported rather than scored.',
    sources: [FLD.hrTimeIsolated, FLD.hrTime, FLD.coi],
  },
  {
    id: 'sec.sab-timer-precision',
    title: 'SharedArrayBuffer-backed timer precision (vs performance.now)',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'sab',
    status: 'implemented',
    probe: 'timerResolution',
    summary:
      'SharedArrayBuffer is gated behind cross-origin isolation for exactly the same reason as high-resolution timers. Comparing a SharedArrayBuffer clock against performance.now() distinguishes "isolation is genuinely enforced" from "isolation is claimed but the timing primitive is still coarse", and flags automation stacks that fail to set up COOP/COEP on the pages they render.',
    limitation: 'Requires cross-origin isolation to measure at all; without it, SharedArrayBuffer is absent and the comparison is not possible.',
    sources: [FLD.coi, FLD.sab],
  },
  {
    id: 'sec.dom-domain',
    title: 'document.domain relaxation removal (version fingerprint)',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'securityHardening',
    summary:
      'Chrome froze document.domain (and later restricted it to public suffixes) as Spectre-adjacent hardening, on a staggered rollout. Whether the legacy relaxation still succeeds, and whether it is silently ignored, is a clean version-boundary fingerprint that is a side effect of mitigation policy rather than a timing channel.',
    limitation: 'A no-op is not always a policy decision — it also reflects the Origin-Agent-Cluster header, so a result must be read alongside that.',
    sources: [FLD.domDomain, FLD.domDomainBlog],
  },
  {
    id: 'sec.site-isolation-timing',
    title: 'Site Isolation cross-process postMessage timing',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'engine',
    status: 'partial',
    probe: 'securityHardening',
    summary:
      'Chrome puts cross-site frames in separate OS processes to contain Spectre-style leaks, so a cross-site postMessage round trip is measurably slower than a same-origin one. Mobile Chrome and low-memory configurations disable or weaken full Site Isolation, making that timing ratio a device-class signal (desktop vs mobile vs embedded WebView) that is essentially unused in bot-detection tooling.',
    limitation:
      'The comparison needs a genuinely cross-site second frame, configured via VITE_CROSS_SITE_FRAME_URL (two *.pages.dev projects qualify, because pages.dev is on the public suffix list; a preview subdomain of the same project is cross-site only by origin, not by site). When that variable is unset the probe reports "not exercised" and contributes no isolation bucket. A srcdoc frame is NOT an opaque origin — it inherits the parent origin — so it only serves as the same-origin baseline. Interpretation is coarse either way: a ratio above ~1.5x is consistent with a process boundary, while ~1.0x can also come from a same-process frame in some configurations. The ratio and bucket are reported as signals, and the bucket is deliberately not hashed: at the 100 µs performance.now() clamp the ratio is quantised and flips between runs in the same browser.',
    sources: [FLD.siteIsolation, FLD.spectre],
  },
  {
    id: 'sec.compression-oracle-proxy',
    title: 'Intermediary recompression as a transforming-proxy tell',
    doc: 'security-research',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'CRIME/BREACH show compressed size leaks plaintext. Repurposed passively: whether a proxy/CDN in front of the client recompresses passthrough content (re-gzipping gzipped bodies, or transcoding brotli to gzip because the intermediary cannot brotli) is a strong "transforming proxy in the middle" signal, distinct from TLS or HTTP framing fingerprints.',
    limitation:
      'Comparing what you send against what reaches the eventual client requires visibility at both ends of the proxy. A static page can only observe its own response, and a compliant browser forbids setting Accept-Encoding, so it cannot request brotli and gzip simultaneously. Needs a server-side vantage point outside the proxy.',
    sources: [FLD.rfc9110, FLD.fetch],
  },
  {
    id: 'sec.h2-single-packet',
    title: 'HTTP/2 single-packet stream coalescing',
    doc: 'security-research',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'How many streams a client genuinely fires inside one TCP segment before the server sees separate reads depends on socket buffering and TLS record coalescing. libuv, the Go scheduler and a browser network thread all behave differently, which makes it an unused network-stack fingerprint.',
    limitation: 'Requires TCP-segment-level observation of the client connection, not something a page can see about itself.',
    sources: [FLD.h2, FLD.tcprfc],
  },
  {
    id: 'sec.tls-session-cache',
    title: 'TLS session-ticket cache size / eviction count',
    doc: 'security-research',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Forcing ticket eviction by driving many distinct SNI values through one connection pool, then counting resumptions before a full handshake, exposes the session-cache size default — which differs between BoringSSL, OpenSSL, rustls and JSSE.',
    limitation: 'Requires server-side handshake state across many connections. Also entangled with the JA4+ patent situation.',
    sources: [FLD.rfc8446],
  },

  // -------------------- security-research-fingerprinting.md §2 OS/network stack
  {
    id: 'sec.tcp-timestamp-skew',
    title: 'TCP timestamp clock-skew (physical-host correlation)',
    doc: 'security-research',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Kohno et al.: any stack with RFC 7323 timestamps exposes a hardware clock drift rate, classically used to prove several IPs are one physical machine. Directly applicable to unmasking supposedly-distinct automation instances that are one box behind rotating proxies — a two-factor farm correlator with the IP-ID axis.',
    limitation: 'Reading the TCP timestamp option and its drift requires raw packet capture. No browser API exposes it, and the same applies to every technique in this section.',
    sources: [FLD.kohno, FLD.rfc7323],
  },
  {
    id: 'sec.ip-id-behavior',
    title: 'IP ID field increment behaviour',
    doc: 'security-research',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Classic p0f material: Linux randomizes per socket, older Windows incremented globally, some stacks are naively sequential. Combined with timestamp skew it gives a same-physical-host correlator stronger than anything at the TLS/HTTP layer.',
    limitation: 'Requires raw packet capture of the IP header.',
    sources: [FLD.p0f],
  },
  {
    id: 'sec.path-mtu',
    title: 'Path MTU / DF-bit and fragment-reassembly timeout',
    doc: 'security-research',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'DF-bit default and how long a stack holds fragments before abandoning reassembly are OS-specific, extremely stable p0f-era signals that are essentially never checked as part of browser fingerprinting.',
    limitation: 'Requires raw packet capture and active fragmentation probing of the client path.',
    sources: [FLD.p0f],
  },
  {
    id: 'sec.source-port-allocation',
    title: 'Source port allocation algorithm (RFC 6056)',
    doc: 'security-research',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Sequential vs random vs hash-based ephemeral port selection differs by kernel version and is observable across many connections from one client — another physical-host correlator on an axis independent of TLS.',
    limitation: 'Requires observing many client source ports server-side.',
    sources: [FLD.rfc6056, FLD.p0f],
  },

  // ------------------------- security-research-fingerprinting.md §3 PKI
  {
    id: 'sec.pq-key-share',
    title: 'Post-quantum hybrid key_share size in the ClientHello',
    doc: 'security-research',
    section: '§3',
    tier: 'edge',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Browsers (Chrome 131+, Firefox 132+) and major libraries (Go 1.24+, OpenSSL 3.5+ and the Python/curl builds linked against it) now send X25519MLKEM768 by default, which inflates the key_share extension and therefore the whole ClientHello.',
    limitation:
      'The edge exposes only tlsClientHelloLength, the total ClientHello size — not the extension breakdown — so a hybrid key share cannot be confirmed, and because adoption is now the default it no longer separates browsers from HTTP libraries either. The old size-class hint was removed as non-discriminating; the raw length remains a transport signal only.',
    sources: [FLD.kyber, FLD.rfc8446psk],
  },
  {
    id: 'sec.aia-chasing',
    title: 'AIA intermediate-certificate chasing (Windows vs OpenSSL)',
    doc: 'security-research',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Windows SChannel/CryptoAPI auto-fetches a missing intermediate from the cert AIA URL; OpenSSL-based stacks generally do not. Serving a deliberately incomplete chain and watching for the AIA follow-up is a very clean "this is CryptoAPI/SChannel" fingerprint, independent of the ClientHello.',
    limitation:
      'Requires serving a deliberately broken chain and observing a second, unexpected request — both server-side, against infrastructure you are authorized to control.',
    sources: [FLD.aia, FLD.rfc8446],
  },
  {
    id: 'sec.ocsp-behaviour',
    title: 'OCSP soft-fail / hard-fail / stapling behaviour',
    doc: 'security-research',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Whether a stack attempts live OCSP lookups, relies on stapling, or how it behaves when the responder is unreachable (including the timeout value) distinguishes OS-native TLS stacks from bundled ones — BoringSSL and rustls typically skip live OCSP entirely.',
    limitation: 'Requires a server with a reachable (or deliberately unreachable) OCSP responder and request-level observation.',
    sources: [FLD.ocsp],
  },
  {
    id: 'sec.crl-vs-ocsp',
    title: 'CRL vs OCSP preference ordering',
    doc: 'security-research',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Legacy, but still occasionally distinguishing: when a certificate offers both, some corporate MITM/proxy appliances reintroduce CRL-only validation.',
    limitation: 'Requires serving a certificate that advertises BOTH a CRL distribution point and an OCSP responder, then observing which endpoint the client actually queries during validation. That needs a TLS terminator you control, per-connection observation, and a browser willing to perform live revocation checking — which most do not by default, so the observable is frequently absent rather than merely hard to reach.',
    sources: [FLD.ocsp],
  },

  // ---------------------- security-research-fingerprinting.md §4 CORS/SOP
  {
    id: 'sec.cors-preflight-cache',
    title: 'Access-Control-Max-Age preflight-cache adherence',
    doc: 'security-research',
    section: '§4',
    tier: 'client',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Whether a client honours the preflight cache and skips a repeat OPTIONS, or re-preflights every time, separates browser fetch implementations from CORS-aware HTTP libraries. Any preflight caching at all is itself a "real browser" signal, and the duration honoured (some browsers cap below the requested value) narrows the version.',
    limitation:
      'Requires a cross-origin endpoint that issues preflights and can count them. A same-origin page never preflights, so this needs a second origin you control.',
    sources: [FLD.cors],
  },
  {
    id: 'sec.coep-enforcement',
    title: 'COEP/CORP enforcement behaviour',
    doc: 'security-research',
    section: '§4',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'sab',
    summary:
      'Whether the client actually blocks a cross-origin no-CORP response from loading in a cross-origin-isolated context is an engine- and version-specific behavioural fingerprint, since COOP/COEP rollout was staggered. Our own site opts into isolation, so whether isolation is genuinely granted (rather than merely requested) is directly observable here via crossOriginIsolated and SharedArrayBuffer availability.',
    limitation:
      'We can verify our own page is (or is not) genuinely isolated; we cannot serve deliberately non-compliant responses to probe another origin without controlling it.',
    sources: [FLD.coi],
  },
  {
    id: 'sec.local-port-scan',
    title: 'Local-network port timing via fetch (opt-in)',
    doc: 'security-research',
    section: '§5',
    tier: 'client',
    layer: 'webrtc',
    status: 'implemented',
    probe: 'portScan',
    summary:
      'Timing differences between connecting to open vs closed vs filtered local ports can reveal that the "client" runs in a locked-down sandbox or headless container rather than a real machine. It runs on every scan: each attempt is an ordinary HTTP GET in no-cors mode to http://127.0.0.1:<port>/ — NOT a bare TCP connect — against seven fixed ports, loopback only, no payload, no routable connection. It contributes nothing to the fingerprint or the score.',
    limitation:
      'Three honest limits. (1) Under Private Network Access and CORS a failure is ambiguous — a closed port and an open-but-blocked port look the same — so the probe reports raw outcomes and explicitly sets classificationAvailable:false. (2) Chrome 142+ may show a Local Network Access permission prompt for public-page loopback fetches; if it is blocked, all seven attempts fail identically, which the probe reports as permissionBlocked / outcomeAmbiguous rather than as port data. (3) Its result is deliberately kept out of the fingerprint and the bot score, because it reflects whichever local services happened to be running at the sampling moment.',
    sources: [FLD.webrtc, FLD.cors, FLD.pnaSource],
  },
  {
    id: 'sec.retry-after',
    title: 'Retry-After adherence precision',
    doc: 'security-research',
    section: '§6',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Serving a 429 with Retry-After and observing whether the client waits exactly that long, waits longer with jitter, ignores it, or gives up separates browsers (which do not auto-retry fetch) from HTTP libraries with retry middleware, each with its own backoff default.',
    limitation:
      'Requires serving 429s and observing a client over time. A browser making a single request never demonstrates this.',
    sources: [FLD.rfc9110],
  },

  // ---------------- security-research-fingerprinting.md §7 kernel-level
  {
    id: 'sec.v8-jit-mitigation',
    title: 'V8 JIT mitigation overhead on typed-array access',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'engine',
    status: 'not-measurable',
    summary:
      'V8 added deliberate masking on out-of-bounds-adjacent typed-array accesses as a Spectre mitigation, imposing small version-dependent overhead that is absent or different in JavaScriptCore, SpiderMonkey and non-browser runtimes. A genuinely under-explored angle versus the usual optimization-tier fingerprinting.',
    limitation:
      'Not implemented, and the reason is specific rather than hand-waved: V8’s pointer masking on out-of-bounds-adjacent typed-array access imposes a small per-access cost that is indistinguishable from ordinary JIT warm-up or a slower CPU in a page. Separating them would need a calibrated benchmark run on a known host under a known JIT tier, which a page cannot guarantee. Adjacent published work (browser-based CPU fingerprinting) does microarchitectural fingerprinting from JS, but not this mitigation-cost signal specifically.',
    sources: [FLD.spectre],
  },
  {
    id: 'sec.kpti-overhead',
    title: 'KPTI syscall overhead as an infrastructure fingerprint',
    doc: 'security-research',
    section: '§7',
    tier: 'server',
    layer: 'network',
    status: 'partial',
    probe: 'infraTiming',
    summary:
      'Kernel Page Table Isolation imposes syscall entry/exit overhead, and cloud VMs stack virtualization overhead on top. Timing variance on rapid small requests can separate patched cloud kernels from bare metal — an independent axis for the "is this actually a cloud farm" question. Grounded in well-documented kernel behaviour, but speculative and noisy over the open internet.',
    limitation:
      'PARTIAL, and the partial part matters: this is an INFRASTRUCTURE signal about the machine serving a request, not about the browser. A page cannot measure the kernel behind its own CDN edge. What IS implemented is the measurement substrate — infraTiming times rapid small requests against a caller-configured origin and reports the latency distribution, which is exactly the raw signal the KPTI idea would consume. The inference step (distribution -> "patched cloud kernel" vs "bare metal ISP") is NOT implemented, because it needs a per-host baseline gathered over time that we deliberately do not collect. Requires a caller-supplied endpoint via VITE_INFRA_ENDPOINT, plus historical baselines, to become decisive.',
    sources: [FLD.spectre],
  },
  {
    id: 'sec.branch-predictor',
    title: 'Branch-predictor state pollution timing channel',
    doc: 'security-research',
    section: '§7',
    tier: 'client',
    layer: 'engine',
    status: 'not-measurable',
    summary:
      'Whether a JS engine build has branch-predictor mitigations (retpolines, IBRS/IBPB) compiled in could distinguish a browser on default OS mitigations from a Node/Bun scraping backend on a host that disabled them for throughput. A known vulnerability-research measurement not previously applied as a bot signal.',
    limitation:
      'Not implemented. The mitigations (retpolines, IBRS/IBPB) are OS/kernel-level, so what a page can actually observe is only their INDIRECT cost on branch-heavy JS. That indirect cost is real but small, and it is confounded by CPU model and frequency scaling, which vary more between machines than the mitigation does. Isolating it needs a fixed-CPU benchmark host plus a mitigation-disabled control — not achievable from a page on arbitrary hardware.',
    sources: [FLD.spectre],
  },

  // ------------------------------- CreepJS-derived (implemented) techniques
  {
    id: 'creep.prototype-lies',
    title: 'JavaScript tampering / prototype lies',
    doc: 'creepjs',
    section: 'lies',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'lies',
    summary:
      'An anti-detect browser replaces native functions with JS shims. A shim is functionally near-perfect but structurally detectable three ways: Function.prototype.toString no longer returns "[native code]", a native function has exactly the own properties length+name, and a native function obeys engine-specific strict-mode error contracts. This is the single most reliable automated-automation tell, and it is hard to defeat because defeating it means not patching anything at all.',
    limitation:
      'We probe a focused set of commonly-shimmed APIs (navigator, canvas, permissions, WebGL, Intl, screen, mediaDevices) rather than dozens of prototypes, to keep the scan fast. A tool that patches only an untested API will not be caught here.',
    sources: [FLD.nikiforakisTampering],
  },
  {
    id: 'creep.engine-stack',
    title: 'Engine stack identification (Blink/Gecko/WebKit × V8/SpiderMonkey/JSC)',
    doc: 'creepjs',
    section: 'engine',
    tier: 'client',
    layer: 'engine',
    status: 'implemented',
    probe: 'engineStack',
    summary:
      'Error-message text and stack-trace formatting are engine-specific, so they identify the script engine for free. Engine-only features (InstallTrigger, window.chrome, webkitStorageInfo, the document.all quirk) identify the layout engine. A User-Agent claiming Firefox while the error strings are unmistakably V8 means a mismatched or spoofed stack.',
    limitation:
      'Classification is heuristic; an unusual or heavily-modified browser can defeat it. Reported alongside the claimed engine so a reader can judge.',
    sources: [FLD.moweryJs, FLD.v8site],
  },
  {
    id: 'creep.css-system-styles',
    title: 'CSS system styles / computed form-control styles',
    doc: 'creepjs',
    section: 'css',
    tier: 'client',
    layer: 'browser',
    status: 'implemented',
    probe: 'css',
    summary:
      'The exact set of CSS properties a UA applies to a bare <input>/<select>/<button> differs by engine AND platform theme, and it survives most spoofing because it lives in the UA stylesheet rather than in any single JS-visible property. A headless or anti-fingerprinting renderer is far more likely to get this wrong than navigator.webdriver.',
    sources: [FLD.cssom],
  },
  {
    id: 'creep.svg-text-metrics',
    title: 'SVG text metrics and sub-pixel DOMRect geometry',
    doc: 'creepjs',
    section: 'svg / domrect',
    tier: 'client',
    layer: 'gpu',
    status: 'implemented',
    probe: 'svgRect',
    summary:
      'SVG text measurement (getComputedTextLength/getExtentOfChar) goes through a different shaping and layout path than canvas fillText, making it an independent fingerprint of the font stack and engine. DOMRect geometry of a fractionally-positioned element exercises the layout engine\'s sub-pixel rounding, which differs per engine and per device scale factor.',
    sources: [FLD.svg2],
  },
  {
    id: 'creep.emoji-rendering',
    title: 'Emoji font rendering fingerprint',
    doc: 'creepjs',
    section: 'samples',
    tier: 'client',
    layer: 'gpu',
    status: 'implemented',
    probe: 'svgRect',
    summary:
      'Emoji render through the platform emoji font (Segoe UI Emoji, Apple Color Emoji, Noto Color Emoji), which is one of the highest-entropy and hardest-to-spoof signals available. Hashing the rendered pixels of a fixed emoji set identifies the platform font directly.',
    sources: [FLD.unicodeEmoji],
  },

  // ------------------------------------------ dns-cache-fingerprinting.md
  {
    id: 'dns.0x20-encoding',
    title: '0x20 case-randomisation as a resolver-software fingerprint',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Some resolvers randomise the case of the query name and verify the response echoes the same casing back, as an anti-cache-poisoning measure (post-Kaminsky). Whether the client’s resolution path does this, and the randomisation pattern, separates BIND, Unbound, systemd-resolved, the Windows DNS client and browser-internal resolvers. A DNS-layer analog of JA3.',
    limitation:
      'The randomisation happens in DNS QUERY packets between the stub resolver and the upstream, and only the RESOLVER can verify the echo. A web page never sees its own query packets, so it cannot detect whether its resolver randomises. Requires DNS traffic visibility: running the resolver, a LAN vantage point, or a cooperating recursive resolver you control.',
    sources: [FLD.dns0x20, FLD.resourceTiming],
  },
  {
    id: 'dns.edns0-fingerprint',
    title: 'EDNS0 fingerprint (UDP buffer size, DO bit, option order)',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'The EDNS0 UDP payload size a resolver advertises (traditionally 4096, moving to 1232 after the 2020/2021 DNS flag day), whether the DNSSEC-OK bit is set, and the presence/ordering of EDNS Cookie (RFC 7873) or padding options are all resolver- and version-specific. A direct DNS equivalent of HTTP header-order fingerprinting.',
    limitation:
      'EDNS0 options live in the DNS query packet. A page cannot see the queries its own resolver emits, so this needs DNS traffic capture or a resolver you operate. We verify empirically that a page gets no EDNS visibility.',
    sources: [FLD.rfc7873, FLD.resourceTiming],
  },
  {
    id: 'dns.doh-canary',
    title: 'DoH capability-check canary query as a browser identifier',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Before auto-enabling DoH, Firefox queries a canary domain (use-application-dns.net) to check whether a network administrator has disabled it; Chrome has its own different probing logic. The presence, exact domain, and timing of that canary query is a near-perfect "this is Firefox, checking DoH policy" signal if DNS traffic is observable.',
    limitation:
      'The canary query is an internal browser-resolver decision, invisible to the page. Only a resolver operator or a network-position observer sees it. We deliberately do NOT attempt to infer it client-side by racing a known canary domain ourselves — that would leak the visitor’s DNS policy and resolver configuration to us for no fingerprinting benefit.',
    sources: [FLD.rfc7871, FLD.resourceTiming],
  },
  {
    id: 'dns.cache-eviction',
    title: 'DNS cache eviction / size probing',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Cause the resolver to cache many hostnames, then probe which are evicted first. Cache size defaults and eviction algorithms (LRU vs pseudo-LRU vs none) differ between dnsmasq, systemd-resolved, Unbound and browser-internal caches; Chrome’s built-in host-resolver cache has its own entry cap and TTL clamping, separate from the OS resolver.',
    limitation:
      'Requires observing resolution behaviour for many distinct hostnames over time, from DNS visibility rather than the web. A page cannot enumerate or probe its resolver’s cache contents.',
    sources: [FLD.he, FLD.resourceTiming],
  },
  {
    id: 'dns.ttl-clamping',
    title: 'TTL clamping as a version-boundary fingerprint',
    doc: 'dns-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'partial',
    probe: 'dnsBehaviour',
    summary:
      'Chrome clamps cached DNS TTLs to its own min/max regardless of what the authoritative server sent, and the clamp has changed across versions. Serving a record with an extreme TTL and measuring when the client re-resolves yields a clamp value specific to browser + version, independent of the TLS/HTTP layer.',
    limitation:
      'PARTIAL: the measurement substrate is implemented (dnsBehaviour reads same-origin domainLookupStart/End, which does expose real DNS resolution timing — verified empirically), but the TTL-clamp INFERENCE is not. Chrome’s clamp is a few seconds and our own origin is not authoritative for a hostname we can vary TTL on from the page side, so we report the resolution timing without deriving a clamp value.',
    sources: [FLD.resourceTiming, FLD.he],
  },
  {
    id: 'dns.cache-sybil-correlation',
    title: 'DNS-layer cache-timing Sybil/farm correlation',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'The DNS-layer analog of the HTTP cache-timing farm detector: many supposedly-distinct clients resolving a shared record with correlated cached-fast timing are likely sharing one resolver/cache egress. The source document rates this among its most novel items, noting the two layers have not been connected in the literature.',
    limitation:
      'Requires correlating resolution timing across MANY visitors over time — a cross-session, server-side analysis. A single page observes only its own resolution and cannot see whether other clients share a resolver. The novelty claim is plausible but unverified here; we found no prior art and no contradicting work either, which is not evidence of novelty.',
    sources: [FLD.he],
  },
  {
    id: 'dns.cache-presence',
    title: 'Does this client cache DNS at all? (naive-script detector)',
    doc: 'dns-cache',
    section: '§2',
    tier: 'client',
    layer: 'network',
    status: 'implemented',
    probe: 'dnsBehaviour',
    summary:
      'A real OS/browser network stack caches resolutions and reuses connections; many naive scraping scripts re-resolve or reconnect on every request. The client-side residue is observable: PerformanceResourceTiming.domainLookupStart === 0 means no lookup was performed for that entry, i.e. the name was already resolved. Implemented for same-origin, where the browser genuinely exposes DNS timing (verified: cross-origin values are clamped to 0 without Timing-Allow-Origin).',
    limitation:
      'We report whether a lookup occurred, but deliberately draw NO verdict about "real browser vs naive script", because every mainstream browser caches DNS — so a single observation cannot separate the two, and claiming otherwise would be a false positive generator. The signal is a component for correlation, not a classifier.',
    sources: [FLD.resourceTiming, FLD.he],
  },
  {
    id: 'dns.rebinding-protection',
    title: 'DNS rebinding protection presence/absence',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Some browsers and router-level resolvers reject a public hostname resolving to a private/loopback address as an anti-rebinding defence. Whether a client blocks or accepts such a response is a clean binary fingerprint of whether real protections are active; bare HTTP libraries typically have none, since it is a browser/router security feature.',
    limitation:
      'TESTED against six free services that provide exactly the cooperating authoritative server this technique needs: nip.io, sslip.io, lvh.me, localho.st and traefik.me (all resolve a public name to 127.0.0.1 / 192.168.1.1 / 10.0.0.1) and, for completeness, the request-callback services interactsh, rbndr.us and dnslog/pingb.cn. All five of the nip.io-family services behave identically, and all fail. Sixteen fetches from the deployed HTTPS page — a loopback LITERAL control (http://127.0.0.1:9/, which the portScan probe depends on and which therefore reaches the TCP layer), the five services over HTTP, and three of them over HTTPS — every single one returned the same opaque "TypeError: Failed to fetch", a single distinct failure mode. The control is the decisive datum: a request known to reach the network is indistinguishable from one that never leaves the browser, so there is no signal to read. Three separate reasons converge on that: (1) MIXED CONTENT — the page is HTTPS, and a literal 127.0.0.1 is a "potentially trustworthy" origin, exempt per the W3C secure-contexts spec, but a hostname merely RESOLVING to loopback is not exempt, so the request is blocked before any rebinding defence is consulted; (2) NO WILDCARD CERTIFICATE — lvh.me, localho.st and traefik.me were all checked and none serves TLS for their subdomains, so the HTTPS variants fail with ECONNREFUSED on 127.0.0.1:443 rather than reaching a rebinding check, meaning the mixed-content exemption cannot be obtained either way; (3) the browser deliberately reports CORS failure, blocked rebinding, refused connection and mixed-content block with one identical error, so even a genuinely differing outcome would be unreadable. The interactsh/rbndr.us/dnslog services are a different category and cannot help at all: they are out-of-band interaction callbacks, useful to a researcher who CONTROLS the domain and reads a log, whereas a visitor\'s page has no way to observe a callback addressed to a third party. The binding constraint is the browser\'s refusal to expose its enforcement reason, not the missing server; a native client, an instrumented resolver, or a cooperating endpoint that returns a distinguishable error would be required.',
    sources: [FLD.dnsRebind],
  },
  {
    id: 'dns.ecs-mismatch',
    title: 'EDNS Client Subnet (ECS) mismatch as a VPN/proxy detector',
    doc: 'dns-cache',
    section: '§2',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Public DoH resolvers attach ECS hints to upstream CDN queries for geo-routing; an ISP-default resolver usually does not, or truncates the subnet. If the ECS subnet the CDN observes does not match the IP the client connects from, that is a strong signal of a DoH resolver inconsistent with the claimed network or VPN exit — useful against "residential proxy" claims that do not hold up end-to-end.',
    limitation:
      'ECS is an EDNS option in the resolver’s query to the authoritative server, and the observable lives at the CDN. A page cannot see the ECS prefix its resolver attached, nor the IP the CDN saw. Requires cooperation from an authoritative server you operate plus visibility into the resolver’s queries.',
    sources: [FLD.rfc7871, FLD.rfc7873],
  },
  {
    id: 'dns.cache-snooping',
    title: 'DNS cache snooping (targeted activity reconnaissance)',
    doc: 'dns-cache',
    section: '§1',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Query a resolver with RD=0 and see whether it answers instantly from cache or resolves fresh (Grangeia, mid-2000s). The source doc’s novel repurposing: against a SPECIFIC client’s own resolver it becomes a passive browsing-activity probe on that target rather than a general resolver-population fingerprint.',
    limitation:
      'Requires sending DNS queries to the target’s resolver with the recursion-desired flag cleared, and the results infer what OTHER users of that resolver have visited. This is targeted activity reconnaissance against a named individual, not fingerprinting for bot detection, and it has a distinct legal/ethical profile from everything else in this catalog. We do not implement it and recommend against building it.',
    sources: [FLD.he],
  },

  // ------------------------------------ certificate-store-fingerprinting.md
  {
    id: 'cert.store-membership',
    title: 'Trust-store membership differential testing',
    doc: 'cert-store',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Chrome (Chrome Root Store), Firefox (Mozilla NSS), Safari (Apple Security.framework) and non-browser stacks (OpenSSL riding the OS store or a bundled ca-certificates package on Linux) ship different root sets, diverging at the margins. Serving a leaf chained to a root present in exactly one or two of them and observing accept/reject cleanly separates browser engines from library TLS stacks.',
    limitation:
      'Requires TERMINATING TLS on a server you control and serving a certificate chained to a specific marginal root, then reading the client’s accept/reject. A static page cannot see the trust decision — the browser reports a generic network error, not which anchor was missing. This is an origin-side active probe.',
    sources: [FLD.rfc5280, FLD.chromeRootStore, FLD.nssRoots],
  },
  {
    id: 'cert.store-battery',
    title: 'Trust-store fingerprinting battery (bit-vector across marginal roots)',
    doc: 'cert-store',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'A set of leaves chained to a range of marginal-trust roots — recently distrusted CAs, CAs pending removal, disputed or short-lived roots, roots with unusual policy constraints — recorded as an accept/reject bit vector. Trust contents differ by OS, browser, patch level and enterprise MDM overlay, so the vector functions as a canvas-fingerprint analog for trust anchors. The source doc notes CA-ecosystem measurement work exists, framed as population research rather than as a client oracle.',
    limitation:
      'Same blocker as store-membership, multiplied: it needs a server presenting dozens of deliberately non-standard chains, plus per-CA maintenance as the marginal set changes. Also the most operationally invasive item in this document — it means standing up an origin that serves chains real browsers will distrust. Not implemented.',
    sources: [FLD.rfc5280, FLD.cabForum, FLD.chromeRootStore],
  },
  {
    id: 'cert.distrust-timing',
    title: 'Root distrust rollout timing as a store-vendor + staleness signal',
    doc: 'cert-store',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'CA distrust events are enforced on different schedules by different vendors, so a client still accepting a chain rooted in a CA distrusted everywhere else pins down both the trust-store vendor and roughly how stale its update state is.',
    limitation:
      'Requires serving a chain to a distrusted root and reading the accept/reject, which means the same origin-side active probe as store-membership. The staleness inference also needs repeated observation over the CA’s removal timeline.',
    sources: [FLD.cabForum, FLD.rfc5280],
  },
  {
    id: 'cert.root-store-gap',
    title: 'Chrome Root Store transition gap (pre/post-2022 Chrome vs Edge vs Safari)',
    doc: 'cert-store',
    section: '§1',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Chrome moved from OS-delegated trust to its own root program on desktop around 2022–2023. Certs trusted by the OS store but not Chrome’s own store (or vice versa) create a detectable gap; testing that gap set distinguishes pre/post-transition Chrome, and separates Chrome from Edge (historically more OS-store-reliant) and Safari (OS keychain only).',
    limitation:
      'Requires a server serving a cert chosen to sit in the OS-store/Chrome-store gap, then reading the accept/reject. A page cannot introspect its own root store at all — there is no Web API exposing trusted anchors.',
    sources: [FLD.chromeRootStore, FLD.nssRoots, FLD.applePkI],
  },
  {
    id: 'cert.store-update-lag',
    title: 'Trust-store update lag as a device-freshness signal',
    doc: 'cert-store',
    section: '§1',
    tier: 'server',
    layer: 'hardware',
    status: 'not-measurable',
    summary:
      'Root stores update on OS patch cycles, so testing against a very recently added or removed root approximates how current a device’s OS/store updates are — correlating with patch level and, by extension, with whether a device is a stripped-down farm image that never receives OS updates versus a regularly-patched consumer machine.',
    limitation:
      'Needs a server serving a very-recently-changed root chain, which means tracking CA program timelines and re-issuing test certs as they change. Not a static-page capability, and operationally heavy.',
    sources: [FLD.cabForum, FLD.rfc5280],
  },
  {
    id: 'cert.multipath-chain',
    title: 'Ambiguous multi-path chain resolution as a chain-builder fingerprint',
    doc: 'cert-store',
    section: '§2',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'When a chain has more than one valid path to trust (the classic case: Let’s Encrypt R3 cross-signed by both ISRG Root X1 and the now-expired DST Root CA X3), different builders pick different paths — Windows CryptoAPI, Security.framework, OpenSSL, BoringSSL and mbedTLS use different heuristics (shortest path, most-trusted root, AKI/SKI preference, newest-non-expired). Serving a deliberately ambiguous chain and observing which path is used identifies the crypto library and OS. The source doc argues the September 2021 DST X3 expiry proves the divergence exists but was analysed as an incident, never repurposed as a technique.',
    limitation:
      'Requires serving a multi-path cross-signed chain and observing the resolution — including which AIA/OCSP endpoint gets hit, which needs a server you control. The page only ever sees an opaque accept/fail. The novelty claim is plausible and the mechanism is well documented, but we have not verified it is unreported.',
    sources: [FLD.rfc5280, FLD.rfc6962],
  },
  {
    id: 'cert.max-validity',
    title: 'Max certificate validity enforcement differences',
    doc: 'cert-store',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Apple has historically been first/strictest to enforce shorter maximum certificate lifetimes (825 days, then 398) ahead of formal CA/Browser Forum baseline changes, with Chrome following its own schedule. Serving a cert whose validity violates a newer vendor threshold and observing who rejects it fingerprints the validator’s policy version.',
    limitation:
      'Requires serving an intentionally over-long certificate and reading whether the client rejects it — an origin-side probe, and one that additionally needs a cert the issuing CA will actually produce. The page sees only a TLS error.',
    sources: [FLD.cabForum, FLD.rfc5280],
  },
  {
    id: 'cert.ct-enforcement',
    title: 'Certificate Transparency (SCT) enforcement presence',
    doc: 'cert-store',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Chrome requires valid SCTs for publicly-trusted certs issued after certain dates; most non-browser HTTP libraries and older browsers do not check CT compliance at all. Serving a chain with missing/invalid SCTs and observing silent acceptance (curl, most server-side libraries) versus rejection (CT-enforcing Chrome) is a discrete check rather than a general compliance concern.',
    limitation:
      'Requires serving a chain with deliberately absent or invalid SCTs — active origin-side TLS misconfiguration. The page cannot see SCT validation; it only sees success or a handshake failure.',
    sources: [FLD.rfc6962, FLD.rfc7633],
  },
  {
    id: 'cert.must-staple',
    title: 'Must-Staple / OCSP enforcement tied to trust-store policy',
    doc: 'cert-store',
    section: '§3',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Distinct from the OCSP soft-fail/hard-fail library behaviour: this is about whether the TRUST STORE’s validation policy treats a missing OCSP staple as a hard failure, which is store-configuration-specific rather than purely library-specific.',
    limitation:
      'Requires serving a must-staple cert without a staple and reading the failure mode. Same origin-side active-probe blocker as the other policy checks.',
    sources: [FLD.rfc6962, FLD.rfc7633],
  },
  {
    id: 'cert.client-cert-request',
    title: 'CertificateRequest CA-list handling by headless automation',
    doc: 'cert-store',
    section: '§4',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'When a server requests a client certificate with an unusual or empty CA list, real interactive browsers show a certificate-picker UI, while headless/automation stacks (Chrome via CDP, Playwright, Selenium) often cannot render it and instead time out, send an empty certificate message, or throw — and WHICH of those three happens is automation-tool-specific, making it a headless-vs-real discriminator that does not rely on JS-level checks at all.',
    limitation:
      'Requires an mTLS endpoint requesting client certificates, which a page cannot trigger for itself (it would need the user to enroll a client cert, and a headless browser under automation would be the one connecting). Genuinely server-tier, and one of the more interesting items in this document.',
    sources: [FLD.rfc5280],
  },
  {
    id: 'cert.client-cert-store',
    title: 'Client cert store enumeration semantics per platform',
    doc: 'cert-store',
    section: '§4',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Whether the platform filters available client certificates against the server’s CA list before presenting them differs between Windows CryptoAPI, macOS Keychain and NSS, observable indirectly by which cert gets offered when multiple candidates exist.',
    limitation:
      'Requires an mTLS server with a specific CA list AND a client with a populated client-cert store. Verified: browsers expose no client-certificate API to script (`navigator` has no such surface), so even the client side cannot introspect its own store.',
    sources: [FLD.rfc5280],
  },
  {
    id: 'cert.enterprise-root',
    title: 'Corporate MITM / injected root CA detection',
    doc: 'cert-store',
    section: '§5',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Corporate proxies and endpoint-security products (Zscaler, Netskope, Fortinet, Palo Alto, TLS-inspection AV modules) inject their own root into the OS or browser trust store, with recognizable Subject/Issuer patterns and often distinctive key parameters. Detecting a known injected root in a presented chain — or noticing the RE-SIGNED leaf has a signature algorithm or key size inconsistent with the real origin — is a strong "this connection passes through corporate TLS inspection" signal.',
    limitation:
      'Requires TLS-terminating on a server you control and inspecting the chain the CLIENT presents, or detecting a MITM root in the chain the client was served. A page cannot inspect its own trust store, and a re-signed leaf is only visible at the TLS layer, not to script. The source doc rightly notes corporate interception is often policy-sanctioned by the device owner, so this is an infrastructure signal, not evidence of compromise.',
    sources: [FLD.rfc5280, FLD.cabForum],
  },
  {
    id: 'cert.android-trust-anchors',
    title: 'Android app-level trust anchor distinctions',
    doc: 'cert-store',
    section: '§5',
    tier: 'server',
    layer: 'tls',
    status: 'not-measurable',
    summary:
      'Since Android N, apps do not trust user- or admin-added CAs unless their network security config opts in. Whether a given Android client trusts an injected root (a corporate MITM, or a testing proxy like Burp/mitmproxy) reveals a hardened native app with a locked-down trust config versus a generic browser or loosely-configured WebView.',
    limitation:
      'Requires serving a chain to a known-injected root and reading whether the Android client accepts it. No Web API exposes the app’s network security config or trust anchors.',
    sources: [FLD.rfc5280],
  },
  {
    id: 'creep.audio-offline',
    title: 'OfflineAudioContext render fingerprint + speech voice inventory',
    doc: 'creepjs',
    section: 'audio / speech',
    tier: 'client',
    layer: 'hardware',
    status: 'implemented',
    probe: 'audioFp',
    summary:
      'Float arithmetic across a fixed audio graph produces slightly different sample values per implementation, and the composite is high-entropy and very hard to spoof because it depends on the whole DSP pipeline. speechSynthesis.getVoices() additionally reveals installed language packs, and is frequently left unpatched by tools that bother to patch canvas.',
    limitation:
      'This is distinct from our AudioWorklet jitter probe: this is the offline render hash (an identity signal), the other is real-time scheduling jitter (a virtualization signal).',
    sources: [FLD.webaudio, FLD.vadrevuAudio],
  },
  {
    id: 'creep.tz-offset-mismatch',
    title: 'Timezone IANA name vs numeric UTC offset',
    doc: 'creepjs',
    section: 'timezone',
    tier: 'client',
    layer: 'locale',
    status: 'implemented',
    probe: 'coherence2',
    summary:
      'The IANA name from Intl.DateTimeFormat().resolvedOptions() and the number from Date.getTimezoneOffset() come from the same engine database, but anti-detect tools routinely patch one and not the other. We also check whether the offset actually changes across a DST boundary for zones that should, and whether the screen size is plausible for the claimed device class.',
    sources: [FLD.intlTimezone],
  },

  // ------------------------------- Paper-derived (catalogued, not shipped)
  {
    id: 'paper.drawnapart',
    title: 'DRAWN APART: remote GPU fingerprinting',
    doc: 'papers',
    section: 'Laperdrix 2022',
    tier: 'client',
    layer: 'gpu',
    status: 'partial',
    probe: 'webglRender',
    summary:
      'Renders a deliberately GPU-stressing scene and fingerprints the result across many devices to identify a GPU model remotely. We render and hash a smaller scene (webglRender) and dump WebGL parameter matrices, but we do not implement the full stress workload or its per-GPU reference database, so we identify a pipeline, not a specific card.',
    limitation: 'Full DRAWN APART needs a large reference corpus of GPU models that the technique itself builds over time.',
    sources: [FLD.drawnApart],
  },
  {
    id: 'paper.clockaroundclock',
    title: 'Clock Around the Clock: time-based device fingerprinting',
    doc: 'papers',
    section: 'Sanchez-Rola 2018',
    tier: 'client',
    layer: 'engine',
    status: 'partial',
    probe: 'timerResolution',
    summary:
      'Timing-based device fingerprinting: the granularity and jitter distribution of high-resolution timers is itself a device+OS+privacy-configuration signature. We measure the performance.now() quantum empirically (timerResolution) but do not build a per-device timing database.',
    sources: ['http://www.eurecom.fr/fr/publication/5664/download/sec-publi-5664.pdf'],
  },
  {
    id: 'paper.shapeshifter',
    title: 'Taming The Shape Shifter: detecting anti-fingerprinting browsers',
    doc: 'papers',
    section: 'Laperdrix 2020',
    tier: 'client',
    layer: 'browser',
    status: 'partial',
    probe: 'lies',
    summary:
      'Antifingerprinting browsers that randomize or spoof attributes are detectable through inconsistencies ACROSS attributes rather than any single one. Our coherence probes (UA vs platform vs GPU vs client hints, cross-context divergence) implement this principle directly.',
    sources: [FLD.shapeShifter],
  },
  {
    id: 'paper.fpstalker',
    title: 'FP-STALKER: tracking fingerprint evolutions',
    doc: 'papers',
    section: 'Laperdrix 2017',
    tier: 'client',
    layer: 'browser',
    status: 'partial',
    probe: 'coherence',
    summary:
      'The same browser configuration drifts over time as the browser updates, which is itself identifying. Our /compare page and per-part sub-hashes let a reader diff two runs over time, which is the practical form of this idea.',
    limitation: 'We do not persist a per-visitor history, so we cannot detect drift for an individual across sessions without the reader storing exports.',
    sources: [FLD.fpStalker],
  },
  {
    id: 'paper.soleva',
    title: 'Prime+Probe 1, JavaScript 0: side-channel defenses',
    doc: 'papers',
    section: 'Shusterman 2021',
    tier: 'client',
    layer: 'engine',
    status: 'not-measurable',
    summary:
      'Prime+Probe reconstructs fine-grained timing despite the defenses that broke naive cache side channels. It is the strongest known technique in this family and is directly relevant to the timer-resolution signal we do measure.',
    limitation:
      'Prime+Probe requires reading micro-architectural state (cache lines) through a covert channel, which a page cannot do reliably in the presence of Spectre-era mitigations, and the technique is designed for cross-origin attacks rather than self-fingerprinting. Catalogued, not implemented.',
    sources: [FLD.soleva],
  },
  {
    id: 'paper.leakingbattery',
    title: 'The Leaking Battery / battery-status privacy',
    doc: 'papers',
    section: 'Olejnik 2015',
    tier: 'client',
    layer: 'hardware',
    status: 'not-measurable',
    summary:
      'navigator.getBattery() was a high-entropy fingerprint (charge level, charging time). It was removed from Firefox and Safari and is deprecated in Chromium, so it is now mostly a "which browser" signal rather than a fingerprinting surface.',
    limitation: 'Removed or unavailable in modern browsers; we detect presence only, via the existing plugin/feature matrix.',
    sources: [FLD.leakingBattery],
  },
  {
    id: 'paper.toronline',
    title: 'Online website fingerprinting: Tor in the real world',
    doc: 'papers',
    section: 'Cherubin 2021',
    tier: 'server',
    layer: 'network',
    status: 'not-measurable',
    summary:
      'Website fingerprinting (classifying a client by the sequence of sites it requests) is a whole-client-profile attack, complementary to attribute fingerprinting.',
    limitation: 'Requires observing a browsing sequence across many sites over time — a server/crawler-level capability, not a single-page signal.',
    sources: [FLD.sjoestenWar],
  },
  {
    id: 'paper.gummybrowsers',
    title: 'Gummy Browsers: targeted browser spoofing',
    doc: 'papers',
    section: 'Liu 2021',
    tier: 'client',
    layer: 'browser',
    status: 'not-measurable',
    summary:
      'A browser can be told to lie in a targeted way (spoof only the attributes an operator checks), which defeats naive coherence checks. Defending against it requires checking attribute pairs an operator would not think to check.',
    limitation:
      'This is an ATTACK paper, not a fingerprinting technique — it describes how to build undetectable spoofing. We catalog it as background that motivates the cross-context and engine-stack checks; we do not implement spoofing.',
    sources: [FLD.gummyBrowsers],
  },
];

export function techniquesByStatus(status: Technique['status']): Technique[] {
  return TECHNIQUES.filter((t) => t.status === status);
}

export function techniquesByTier(tier: Tier): Technique[] {
  return TECHNIQUES.filter((t) => t.tier === tier);
}
